import { Supabase } from "../Utils/supabase.config";
import RNFS from 'react-native-fs';
import { Share, Platform } from 'react-native';
import { supabaseUrl } from "../constants";
import { compressImage, compressVideo } from "./compressionService";

// Convert base64 to ArrayBuffer directly
const base64ToArrayBuffer = (base64) => {
    try {
        // Try using global atob if available
        if (global.atob) {
            const binaryString = global.atob(base64);
            const bytes = new Uint8Array(binaryString.length);
            for (let i = 0; i < binaryString.length; i++) {
                bytes[i] = binaryString.charCodeAt(i);
            }
            return bytes.buffer;
        }
        
        // Fallback implementation for React Native
        const chars = 'ABCDEFGHIJKLMNOPQRSTUVWXYZabcdefghijklmnopqrstuvwxyz0123456789+/=';
        let output = '';
        let str = String(base64).replace(/=+$/, '');
        if (str.length % 4 === 1) {
            throw new Error('Invalid base64 string');
        }
        for (let bc = 0, bs = 0, buffer, i = 0; buffer = str.charAt(i++);) {
            if (buffer = chars.indexOf(buffer)) {
                bs = bc % 4 ? bs * 64 + buffer : buffer;
                if (bc++ % 4) output += String.fromCharCode(255 & bs >> (-2 * bc & 6));
            }
        }
        
        const bytes = new Uint8Array(output.length);
        for (let i = 0; i < output.length; i++) {
            bytes[i] = output.charCodeAt(i);
        }
        return bytes.buffer;
    } catch (e) {
        // console.log('Error in base64ToArrayBuffer:', e);
        throw e;
    }
};

// Alternative base64 to ArrayBuffer using Buffer (if available)
const base64ToArrayBufferAlternative = (base64) => {
    try {
        // If Buffer is available (Node.js environment)
        if (global.Buffer) {
            return global.Buffer.from(base64, 'base64').buffer;
        }
        
        // Fallback to manual conversion
        const binaryString = atob(base64);
        const len = binaryString.length;
        const bytes = new Uint8Array(len);
        for (let i = 0; i < len; i++) {
            bytes[i] = binaryString.charCodeAt(i);
        }
        return bytes.buffer;
    } catch (e) {
        // console.log('Error in base64ToArrayBufferAlternative:', e);
        throw e;
    }
};

export const uploadFile = async (bucketName, fileUri, isImage=true, originalFileName=null, folderName='', shouldCompress=true)=>{
    try{
        // Compress image/video before upload (only for post media, not profiles)
        let processedUri = fileUri;
        if (shouldCompress && (folderName === 'postImages' || folderName === 'postVideos' || folderName === 'uploads')) {
            try {
                if (isImage) {
                    const compressResult = await compressImage(fileUri, {
                        maxWidth: 1920,
                        maxHeight: 1920,
                        quality: 0.8,
                        outputFormat: 'jpeg'
                    });
                    if (compressResult.success) {
                        processedUri = compressResult.data;
                    } else {
                        // If compression fails, continue with original
                        console.warn('Image compression failed, using original:', compressResult.msg);
                    }
                } else {
                    const compressResult = await compressVideo(fileUri, {
                        maxWidth: 1920,
                        maxHeight: 1080,
                        bitrate: 2000000 // 2 Mbps
                    });
                    if (compressResult.success) {
                        processedUri = compressResult.data;
                    } else {
                        // If compression fails, continue with original
                        console.warn('Video compression failed, using original:', compressResult.msg);
                    }
                }
            } catch (compressionError) {
                // If compression throws an error, continue with original file
                console.warn('Compression error, using original file:', compressionError);
                processedUri = fileUri;
            }
        }

        // console.log('Starting file upload for:', processedUri);
        let fileName = getFilePath(folderName, isImage, originalFileName); // e.g. /community-profiles/123.jpg
        const filePath = fileName.startsWith('/') ? fileName.slice(1) : fileName; // remove leading slash
        // console.log('Generated filename:', fileName);
        
        // Determine content type based on file extension
        const getContentType = (filename) => {
            if (!filename) return isImage ? 'image/jpeg' : 'video/mp4';
            const extension = filename.split('.').pop()?.toLowerCase();
            switch (extension) {
                case 'jpg':
                case 'jpeg':
                    return 'image/jpeg';
                case 'png':
                    return 'image/png';
                case 'gif':
                    return 'image/gif';
                case 'webp':
                    return 'image/webp';
                case 'mp4':
                    return 'video/mp4';
                case 'mov':
                    return 'video/quicktime';
                default:
                    return isImage ? 'image/jpeg' : 'video/mp4';
            }
        };
        
        const contentType = getContentType(originalFileName);
        // console.log('Detected content type:', contentType);

        // Prepare FormData and file payload differently for web vs native
        const formData = new FormData();

        if (Platform.OS === 'web') {
            // On web, processedUri will usually be a Data URL from the file picker or compression
            let blob;

            if (typeof processedUri === 'string' && processedUri.startsWith('data:')) {
                // Extract base64 data from Data URL
                const base64Data = processedUri.split(',')[1];
                if (!base64Data) {
                    return { success: false, msg: 'Invalid data URL for upload' };
                }

                // Convert base64 to ArrayBuffer, then to Blob
                let arrayBuffer;
                try {
                    arrayBuffer = base64ToArrayBuffer(base64Data);
                } catch (e) {
                    try {
                        arrayBuffer = base64ToArrayBufferAlternative(base64Data);
                    } catch (e2) {
                        return { success: false, msg: 'Could not process file data for upload' };
                    }
                }
                blob = new Blob([arrayBuffer], { type: contentType });
            } else {
                // If it's not a Data URL, try fetching it as a Blob (e.g., remote URL)
                const resp = await fetch(processedUri);
                if (!resp.ok) {
                    return { success: false, msg: 'Could not fetch file for upload' };
                }
                blob = await resp.blob();
            }

            formData.append('file', blob, originalFileName || 'upload');
        } else {
            // Native (Android/iOS) - use RNFS + URI style
        // Check if file exists
        const fileExists = await RNFS.exists(processedUri);
        if (!fileExists) {
            // console.log('File does not exist:', processedUri);
            return {success: false, msg: "File does not exist"};
        }

        formData.append('file', {
            uri: processedUri,
            name: originalFileName || 'photo.jpg',
            type: contentType,
        });
        }

        // Get session for access token
        const { data: { session } } = await Supabase.auth.getSession();
        const anonKey = 'eyJhbGciOiJIUzI1NiIsInR5cCI6IkpXVCJ9.eyJpc3MiOiJzdXBhYmFzZSIsInJlZiI6ImhlZHBsY2Vld3Fqcm1ycHhibG5rIiwicm9sZSI6ImFub24iLCJpYXQiOjE3NTA3NTY5MzMsImV4cCI6MjA2NjMzMjkzM30.8V6gOcMJFJBNRGtnwuQjnSW4PKzSgw42yknwSXQN1ek';
        
        // Determine the correct path based on the folderName
        let uploadPath = filePath;
        if (folderName === 'community-profiles') {
            uploadPath = `community-profiles/${filePath.replace('community-profiles/', '')}`;
        } else if (folderName === 'profiles') {
            uploadPath = `profiles/${filePath.replace('profiles/', '')}`;
        } else if (isImage) {
            uploadPath = `postImages/${filePath}`;
        } else {
            uploadPath = `postVideos/${filePath}`;
        }
        
        const uploadUrl = `https://hedplceewqjrmrpxblnk.supabase.co/storage/v1/object/uploads/${uploadPath}`;
        // console.log('Upload URL:', uploadUrl);
        
        const response = await fetch(uploadUrl, {
            method: 'POST',
            headers: {
                'Authorization': `Bearer ${session.access_token}`,
                'apikey': anonKey,
                // Let the browser set the correct multipart boundary on web;
                // on native, this is also safe to omit as fetch will handle it.
            },
            body: formData,
        });

        if (!response.ok) {
            const errorText = await response.text();
            // console.log('REST API error response:', errorText);
            throw new Error(`REST API failed: ${response.status} - ${errorText}`);
        }

        const result = await response.json();
        const uploadedPath = result.Key ? result.Key.replace('uploads/', '') : uploadPath;
        return {success: true, data: uploadedPath};
    } catch (error) {
        // console.log('File upload error:', error);
        return {success: false, msg: error.message || "Could not upload media"};
    }
}

export const getFilePath = (folderName, isImage=true, originalFileName=null)=>{
    const timestamp = (new Date()).getTime();
    if (isImage) {
        // Try to preserve the original file extension
        if (originalFileName) {
            const extension = originalFileName.split('.').pop()?.toLowerCase();
            if (extension && ['jpg', 'jpeg', 'png', 'gif', 'webp'].includes(extension)) {
                return folderName ? `${folderName}/${timestamp}.${extension}` : `${timestamp}.${extension}`;
            }
        }
        // Default to jpg for images
        return folderName ? `${folderName}/${timestamp}.jpg` : `${timestamp}.jpg`;
    }
    return folderName ? `${folderName}/${timestamp}.mp4` : `${timestamp}.mp4`;
}

export const getUserImageSrc = (imagePath) => {
    if (
        imagePath &&
        imagePath.trim() !== '' &&
        !imagePath.endsWith('defaultuser.png')
    ) {
        const result = getSupabaseFileUrl(imagePath);
        if (result) return result;
    }
    try {
        // Use a placeholder or return undefined on web
        return undefined;
    } catch (e) {
        // console.error('Fallback image missing or path incorrect:', e);
        return undefined;
    }
};

export const getSupabaseFileUrl = filePath => {
    if (filePath && filePath.trim() !== '') {
        // Remove leading slash if present to avoid double slashes
        const cleanPath = filePath.startsWith('/') ? filePath.substring(1) : filePath;
        
        // All files are stored in the uploads bucket with specific folders
        const url = `https://hedplceewqjrmrpxblnk.supabase.co/storage/v1/object/public/uploads/${cleanPath}`;
        
        // REMOVED: HEAD request check - this was causing thousands of unnecessary network requests
        // The browser will handle 404s naturally, and we don't need to pre-check every URL
        // This was the main cause of 18,000+ network requests!
        
        return { uri: url };
    }
    // fallback to a local image
    return undefined;
};

export const downloadFile = async (url)=>{
    try {
        // Start the download
        const { uri } = await RNFS.downloadFile(url, getLocalFilePath(url));
        return uri;
    } catch (e) {
        return null;
    }
}

const getLocalFilePath = filePath=>{
    let fileName = filePath.split('/').pop();
    return `${RNFS.documentDirectory}${fileName}`;
}